import React, { useContext, useRef } from "react"
import { View, StyleSheet, Image, Animated } from "react-native"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import logo from "@assets/logo-blue.png"
import FunnelScene, {
    FunnelSceneCaption,
    SCENE_BACKGROUND,
} from "./FunnelScene"
import { WIDE_BREAKPOINT } from "@common/components/funnel/FunnelQuestionScreen"

// Au-delà, l'écran a la place de porter la scène à gauche et le contenu à
// droite — le même point de bascule que les écrans de question, pour que le
// funnel ne change pas de gabarit d'une étape à l'autre.
const WIDE_CONTENT_MAX = 560
const WIDE_LOGO_BOTTOM = 24
const WIDE_LOGO_HEIGHT = 28
const WIDE_CONTENT_PADDING = 40
const CONTENT_MAX = 520
const CONTENT_PADDING = 20
/** Hauteur du bandeau mobile : assez pour une scène, pas au point de repousser la question. */
const BAND_HEIGHT = 210

/**
 * La largeur utile du contenu, dans les deux gabarits.
 *
 * Exportée pour les rares enfants qui se dimensionnent en dur — le checkout
 * Stripe pose deux largeurs fixes. Sans elle ils se calaient sur la FENÊTRE, et
 * débordaient de leur colonne sur grand écran ; la calculer à part dans l'écran
 * a produit l'erreur inverse (une constante supprimée d'un côté, encore lue de
 * l'autre, donc un crash visible seulement en dessous du point de bascule).
 * Une seule fonction, les deux branches.
 */
/**
 * Ce que le contenu défilant porte AU-DESSUS de la feuille sur petit écran :
 * l'espaceur qui réserve la place du bandeau. Un écran qui calcule une position
 * de défilement depuis ses propres `onLayout` (mesurés dans la feuille) doit
 * l'ajouter, sinon il vise trop court d'un bandeau.
 */
export const bandSpacerHeight = (windowWidth) =>
    windowWidth >= WIDE_BREAKPOINT ? 0 : BAND_HEIGHT + 18

export const funnelContentWidth = (windowWidth) =>
    windowWidth >= WIDE_BREAKPOINT
        ? WIDE_CONTENT_MAX - WIDE_CONTENT_PADDING * 2
        : Math.min(windowWidth || CONTENT_MAX, CONTENT_MAX) -
          CONTENT_PADDING * 2

/**
 * Le gabarit d'UNE étape d'un funnel à scènes — le stage et la Rentrée le lisent.
 *
 * Grand écran : la scène occupe toute la colonne de gauche, le contenu défile à
 * droite. Petit écran : la scène devient un bandeau plein bord en tête, et le
 * contenu passe dessous. Un seul endroit pour les deux, parce que sept écrans
 * qui réimplémentent chacun leur split finiraient par ne plus se ressembler.
 *
 * **Pourquoi une scène et pas une photo** : une photo porte cent détails et ne
 * peut illustrer que « un élève existe ». Une scène — un aplat, un humain ou un
 * objet, parfois un agent — dit ce que l'écran demande, et reste lisible sur
 * 210 px de haut. Le contenu des scènes vit dans `FUNNEL_SCENES`.
 *
 * `overlay` reçoit les modales de l'écran : elles doivent être SŒURS du contenu,
 * pas dans le ScrollView, sinon elles défilent avec lui.
 */
const FunnelStepLayout = ({
    scene,
    header,
    footer,
    children,
    overlay,
    scrollRef,
    contentContainerStyle,
    scroll = true,
}) => {
    const dimensions = useContext(DimensionsContext)
    const windowWidth = dimensions?.width ?? 0
    const isWide = windowWidth >= WIDE_BREAKPOINT

    // Le bandeau mobile se REPLIE au scroll, comme celui des écrans de question :
    // il est posé en absolu sous l'en-tête, sa hauteur suit le défilement, la
    // scène remonte un peu plus vite (parallaxe) et s'efface ; un espaceur
    // transparent en tête du contenu lui réserve sa place au repos. Sans ça,
    // 210 px de scène + l'en-tête laissent à un téléphone une zone de lecture
    // trop courte pour composer quoi que ce soit.
    const scrollY = useRef(new Animated.Value(0)).current
    const scrollProps = {
        onScroll: Animated.event(
            [{ nativeEvent: { contentOffset: { y: scrollY } } }],
            { useNativeDriver: false }
        ),
        scrollEventThrottle: 16,
    }
    const bandHeight = scrollY.interpolate({
        inputRange: [0, BAND_HEIGHT],
        outputRange: [BAND_HEIGHT, 0],
        extrapolate: "clamp",
    })
    const sceneShift = scrollY.interpolate({
        inputRange: [0, BAND_HEIGHT],
        outputRange: [0, -BAND_HEIGHT * 0.45],
        extrapolate: "clamp",
    })
    const sceneOpacity = scrollY.interpolate({
        inputRange: [0, BAND_HEIGHT * 0.7],
        outputRange: [1, 0],
        extrapolate: "clamp",
    })
    // Sur grand écran, la scène vit dans le panneau de gauche : rien à
    // réserver en tête du contenu — sinon un écran qui rend l'espaceur
    // lui-même (le checkout) ouvrait sur 228 px de blanc.
    const spacer =
        scene && !isWide ? (
            <View style={styles.bandSpacer} pointerEvents="none" />
        ) : null

    // `scroll={false}` : l'écran apporte son propre défilement (le checkout
    // Stripe, par exemple). Deux ScrollView imbriqués se disputent le geste.
    // Il reçoit alors, en fonction, de quoi faire suivre le bandeau : les
    // props de scroll à poser sur SON ScrollView, et l'espaceur à rendre en
    // tête de son contenu.
    const content =
        typeof children === "function"
            ? children({ scrollProps, spacer })
            : children

    if (isWide) {
        return (
            <View style={styles.wideRoot}>
                <View style={styles.widePanel}>
                    {scene ? (
                        <FunnelScene
                            scene={scene}
                            variant="desktop"
                            style={styles.wideScene}
                        />
                    ) : null}
                    {scene?.caption ? (
                        <FunnelSceneCaption
                            scene={scene}
                            style={styles.wideCaption}
                        />
                    ) : null}
                    <Image
                        source={logo}
                        style={styles.wideLogo}
                        resizeMode="contain"
                    />
                </View>

                <View style={styles.wideContentCol}>
                    <View style={styles.wideContentInner}>
                        {header}
                        {scroll ? (
                            <Animated.ScrollView
                                ref={scrollRef}
                                contentContainerStyle={[
                                    styles.wideScrollContent,
                                    contentContainerStyle,
                                ]}
                                showsVerticalScrollIndicator={false}
                            >
                                {content}
                            </Animated.ScrollView>
                        ) : (
                            <View style={styles.plainContentWide}>
                                {content}
                            </View>
                        )}
                        {footer}
                    </View>
                </View>
                {overlay}
            </View>
        )
    }

    const containerWidth = Math.min(windowWidth || CONTENT_MAX, CONTENT_MAX)
    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                <View style={styles.gutter}>{header}</View>
                <View style={styles.body}>
                    {scene ? (
                        <Animated.View
                            style={[styles.bandLayer, { height: bandHeight }]}
                            pointerEvents="none"
                        >
                            <Animated.View
                                style={{
                                    height: BAND_HEIGHT,
                                    opacity: sceneOpacity,
                                    transform: [{ translateY: sceneShift }],
                                }}
                            >
                                <FunnelScene
                                    scene={scene}
                                    variant="mobile"
                                    style={styles.band}
                                />
                            </Animated.View>
                        </Animated.View>
                    ) : null}
                    {scroll ? (
                        <Animated.ScrollView
                            ref={scrollRef}
                            {...scrollProps}
                            contentContainerStyle={[
                                styles.scrollContent,
                                contentContainerStyle,
                            ]}
                            showsVerticalScrollIndicator={false}
                        >
                            {spacer}
                            <View style={styles.sheet}>{content}</View>
                        </Animated.ScrollView>
                    ) : (
                        <View style={styles.plainContent}>
                            {typeof children === "function" ? null : spacer}
                            {content}
                        </View>
                    )}
                </View>
                <View style={styles.gutter}>{footer}</View>
            </View>
            {overlay}
        </View>
    )
}

const styles = StyleSheet.create({
    // --- grand écran ---
    wideRoot: {
        flex: 1,
        flexDirection: "row",
        backgroundColor: theme.colors.white,
    },
    widePanel: {
        flex: 1,
        backgroundColor: SCENE_BACKGROUND,
        position: "relative",
        overflow: "hidden",
    },
    wideScene: { flex: 1 },
    wideLogo: {
        position: "absolute",
        bottom: WIDE_LOGO_BOTTOM,
        left: 24,
        width: 96,
        height: WIDE_LOGO_HEIGHT,
    },
    // Sous la scène, au-dessus du logo.
    wideCaption: {
        marginHorizontal: "6%",
        marginBottom: WIDE_LOGO_BOTTOM + WIDE_LOGO_HEIGHT + 20,
    },
    wideContentCol: {
        flex: 1,
        backgroundColor: theme.colors.white,
        alignItems: "center",
    },
    wideContentInner: {
        flex: 1,
        width: "100%",
        maxWidth: WIDE_CONTENT_MAX,
        paddingHorizontal: WIDE_CONTENT_PADDING,
    },
    wideScrollContent: { paddingTop: 40, paddingBottom: 28 },
    // Le contenu qui défile tout seul (checkout) garde la gouttière du gabarit :
    // sans elle, ses cartes touchent les bords de l'écran.
    plainContent: { flex: 1, paddingHorizontal: CONTENT_PADDING },
    // La colonne large porte déjà sa gouttière (`wideContentInner`).
    plainContentWide: { flex: 1 },

    // --- petit écran ---
    root: {
        flex: 1,
        alignItems: "center",
        backgroundColor: theme.colors.white,
    },
    frame: { flex: 1 },
    body: { flex: 1, position: "relative" },
    gutter: { paddingHorizontal: CONTENT_PADDING },
    // Le bandeau vit sous le contenu, plein bord, clippé par sa hauteur animée.
    bandLayer: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        overflow: "hidden",
        backgroundColor: SCENE_BACKGROUND,
    },
    band: { height: BAND_HEIGHT },
    // L'espaceur réserve la place du bandeau au repos ; le contenu défile
    // par-dessus, sur fond blanc.
    bandSpacer: { height: BAND_HEIGHT + 18 },
    scrollContent: { paddingBottom: 28 },
    sheet: {
        paddingHorizontal: CONTENT_PADDING,
        backgroundColor: theme.colors.white,
    },
})

export default FunnelStepLayout
